import CustomArrowShape from "@/components/CustomArrowShape";
import { useWindowSize } from "@/hooks";
import { PageType } from "@/services/shared-models";
import { useNode } from "@craftjs/core";
import { Box, Typography } from "@mui/material";
import { ReactNode, useCallback, useLayoutEffect, useState } from "react";
import Xarrow, { Xwrapper } from "react-xarrows";
import Settings from "./Settings";

interface PageProps {
    parentId?: string;
    parentOrder?: number;
    inverseOrder: boolean;
    pageType: PageType;
    title: string;
    slug: string;
    language: string;
    comments: "yes" | "no";
    children: ReactNode;
}

const Page = ({ title, pageType, children }: PageProps) => {
    const {
        connectors: { connect },
    } = useNode();

    const [width] = useWindowSize();
    const [left, setLeft] = useState(0);

    const moveBackground = useCallback(() => {
        const editorContainer = document.querySelector("#editor-container");
        let pageWidth = width;
        let editorOffset = 0;
        if (editorContainer) {
            const rendererContainer = document.querySelector(".craftjs-renderer");
            const rect = editorContainer.getBoundingClientRect();
            editorOffset = rect.left + 56;
            pageWidth = rendererContainer?.clientWidth || width;
        }

        const change = 2560 - pageWidth - editorOffset;
        setLeft(change * -0.5);
    }, [width]);

    useLayoutEffect(() => {
        document.addEventListener("fullscreenchange", moveBackground);

        return () => {
            document.removeEventListener("fullscreenchange", moveBackground);
        };
    }, [moveBackground]);

    useLayoutEffect(() => {
        moveBackground();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [width]);

    const renderPage = () => {
        switch (pageType) {
            case PageType.Home:
                return (
                    <>
                        <div
                            className={`absolute w-full h-[690px] bg-primary-100 overflow-hidden`}
                            style={{
                                left: `${left}px`,
                                width: width + Math.abs(left),
                                clipPath: "polygon(220px 0, 2350px 0, 715px 690px)",
                            }}
                        />

                        {children}
                    </>
                );

            case PageType.Step1:
                return (
                    <>
                        <div
                            className={`absolute w-full h-[500px] bg-primary-100 overflow-hidden`}
                            style={{
                                left: `${left}px`,
                                width: 2015,
                                clipPath: "polygon(200px 0, 2015px 0, 838px 450px)",
                            }}
                        />

                        {children}

                        <Xarrow
                            start="lead-0"
                            end="lead-6"
                            color="#8dd6d0"
                            dashness={{ strokeLen: 15, nonStrokeLen: 15 }}
                            strokeWidth={5}
                            startAnchor={"auto"}
                            endAnchor={"top"}
                            headSize={0}
                            showHead={false}
                            path="straight"
                        />

                        <Xarrow
                            start="lead-6"
                            end="lead-7"
                            color="#8dd6d0"
                            dashness={{ strokeLen: 15, nonStrokeLen: 15 }}
                            strokeWidth={5}
                            startAnchor={"bottom"}
                            endAnchor={{
                                position: "left",
                                offset: { x: -30, y: 0 },
                            }}
                            headSize={1}
                            showHead={true}
                            headShape={CustomArrowShape}
                            path="smooth"
                            curveness={0.95}
                        />

                        <Xarrow
                            start="lead-8"
                            end="lead-spotlight"
                            color="#8dd6d0"
                            dashness={{ strokeLen: 15, nonStrokeLen: 15 }}
                            strokeWidth={5}
                            startAnchor={"top"}
                            endAnchor={"top"}
                            showHead={true}
                            headShape={CustomArrowShape}
                            headSize={1}
                            showTail={true}
                            tailShape={"circle"}
                            tailSize={5}
                            path="straight"
                        />
                    </>
                );

            case PageType.Step2:
                return (
                    <>
                        <div
                            className={`absolute w-full h-[500px] bg-primary-100 overflow-hidden`}
                            style={{
                                left: `${left}px`,
                                width: 2015,
                                clipPath: "polygon(200px 0, 2015px 0, 838px 450px)",
                            }}
                        />

                        {children}

                        <Xarrow
                            start="lead-0"
                            end="lead-5"
                            color="#8dd6d0"
                            dashness={{ strokeLen: 15, nonStrokeLen: 15 }}
                            strokeWidth={5}
                            startAnchor={"auto"}
                            endAnchor={"top"}
                            headSize={0}
                            showHead={false}
                            path="straight"
                        />

                        <Xarrow
                            start="lead-6"
                            end="lead-spotlight"
                            color="#8dd6d0"
                            dashness={{ strokeLen: 15, nonStrokeLen: 15 }}
                            strokeWidth={5}
                            startAnchor={"top"}
                            endAnchor={"top"}
                            showHead={true}
                            headShape={CustomArrowShape}
                            headSize={1}
                            showTail={true}
                            tailShape={"circle"}
                            tailSize={5}
                            path="straight"
                            curveness={0.95}
                        />
                    </>
                );

            case PageType.Step3:
                return (
                    <>
                        <div
                            className={`absolute w-full h-[500px] bg-primary-100 overflow-hidden`}
                            style={{
                                left: `${left}px`,
                                width: 2015,
                                clipPath: "polygon(200px 0, 2015px 0, 838px 450px)",
                            }}
                        />

                        {children}

                        <Xarrow
                            start="lead-0"
                            end="lead-3"
                            color="#8dd6d0"
                            dashness={{ strokeLen: 15, nonStrokeLen: 15 }}
                            strokeWidth={5}
                            startAnchor={"auto"}
                            endAnchor={"top"}
                            headSize={0}
                            showHead={false}
                            path="straight"
                        />

                        <Xarrow
                            start="lead-3"
                            end="lead-4"
                            color="#8dd6d0"
                            dashness={{ strokeLen: 15, nonStrokeLen: 15 }}
                            strokeWidth={5}
                            startAnchor={"bottom"}
                            endAnchor={{
                                position: "left",
                                offset: { x: -20, y: 0 },
                            }}
                            headSize={1}
                            showHead={true}
                            headShape={CustomArrowShape}
                            path="smooth"
                            curveness={0.9}
                        />
                    </>
                );

            case PageType.Master:
                return (
                    <Box sx={{ display: "flex", height: 200, alignItems: "center" }}>
                        <Box sx={{ m: "auto", width: "50%" }}>
                            <Typography variant="h5" sx={{ mb: 2 }}>
                                {title} - Master Page
                            </Typography>
                            <Typography variant="body2">
                                This page functions as a group for one or more child pages. When this page is viewed by
                                the end-user it will load the content of the first child page.
                            </Typography>
                        </Box>
                    </Box>
                );

            case PageType.Elements:
                return (
                    <Box sx={{ display: "flex", height: 200, alignItems: "center" }}>
                        <Box sx={{ m: "auto", width: "50%" }}>
                            <Typography variant="h5" sx={{ mb: 2 }}>
                                {title}
                            </Typography>
                            <Typography variant="body2">
                                This page will render a list of all elements available in the system for the selected
                                language of the user.
                            </Typography>
                        </Box>
                    </Box>
                );

            case PageType.Download:
                return (
                    <>
                        <div
                            className={`absolute w-full h-[500px] bg-primary-100 overflow-hidden`}
                            style={{
                                left: `${left}px`,
                                width: 2015,
                                clipPath: "polygon(200px 0, 2015px 0, 838px 450px)",
                            }}
                        />

                        {children}

                        <Xarrow
                            start="lead-start"
                            end="lead-spotlight"
                            color="#8dd6d0"
                            dashness={{ strokeLen: 15, nonStrokeLen: 15 }}
                            strokeWidth={5}
                            startAnchor={"top"}
                            endAnchor={"top"}
                            showHead={true}
                            headShape={CustomArrowShape}
                            headSize={1}
                            showTail={true}
                            tailShape={"circle"}
                            tailSize={5}
                            path="straight"
                            curveness={0.95}
                        />
                    </>
                );

            case PageType.Content:
                return (
                    <Xwrapper>
                        <div
                            className={`absolute w-full h-[500px] bg-primary-100 overflow-hidden`}
                            style={{
                                left: `${left}px`,
                                width: width + Math.abs(left),
                                clipPath: "polygon(200px 0, 2015px 0, 838px 450px)",
                            }}
                        />

                        {children}
                    </Xwrapper>
                );

            default:
                throw new Error(`Page type ${pageType} not implemented`);
        }
    };

    return (
        <div ref={(ref) => connect(ref!)}>
            <Xwrapper>{renderPage()}</Xwrapper>
        </div>
    );
};

Page.craft = {
    displayName: "Page",
    props: {
        parentId: null,
        parentOrder: null,
        inverseOrder: false,
        title: "Untitled page",
        slug: "page-slug",
        language: "int-en",
        pageType: PageType.Content,
        comments: "yes",
    },
    rules: {
        canDrag: () => false,
        canMoveIn: () => false,
        canMoveOut: () => false,
    },
    related: {
        settings: Settings,
    },
};

export default Page;
